import { useCallback, useEffect, useRef, useState } from 'react';
import { StudyTypeCard } from './components/StudyTypeCard';
import { MethodologyChecklist } from './components/MethodologyChecklist';
import { IntegrityFindings } from './components/IntegrityFindings';
import { ReviewerSearchPanel } from './components/ReviewerSearchPanel';
import { DecisionCard } from './components/DecisionCard';
import { PdfDropZone } from './components/PdfDropZone';
import { SettingsPanel } from './components/SettingsPanel';
import { AboutPanel } from './components/AboutPanel';
import { ExportBar } from './components/ExportBar';
import { OnboardingCard } from './components/OnboardingCard';
import { TextInputCard } from './components/TextInputCard';
import { LlmStatusCard } from './components/LlmStatusCard';
import { EnrichmentStatusCard } from './components/EnrichmentStatusCard';
import {
  extractFromArrayBuffer,
  extractFromText,
  type ManuscriptDocument,
} from '../lib/pdf';
import {
  findByHash,
  findingsBySkim,
  upsert,
  upsertFindings,
  patch,
  makeReport,
  bulkInsertCandidates,
  candidatesBySkim,
  setCandidateSelection,
  addManualCandidate,
  removeCandidate,
  updateFindingAction,
  logAuditEvent,
  getAllSettings,
  setSetting,
  type UserSettings,
  type SkimReportRecord,
  type SkimFindingRecord,
  type ReviewerCandidateRecord,
} from '../lib/storage';
import { runPatternAnalysis, type PatternFinding, type PatternRunResult } from '../lib/patterns';
import {
  runApiAnalysis,
  type ApiAnalysisResult,
  type ReviewerCandidate,
  type ReviewerSearchResult,
} from '../lib/apis';
import {
  generateEmail,
  generatePdf,
  generateJson,
  copyToClipboard,
  triggerDownload,
  suggestDecision,
  type DecisionLabel,
  type EditorDraft,
  type ExportInput,
} from '../lib/export';
import {
  getLlmClient,
  configureLlm,
  runLlmEnrichment,
  runClaimSupportPass,
  draftDecisionRationale,
  DEFAULT_MODEL_ID,
  type EngineState,
  type LlmEnrichmentResult,
} from '../lib/llm';

type Status =
  | { kind: 'idle' }
  | { kind: 'extracting'; fileName: string; progress: number }
  | { kind: 'analysing'; fileName: string; doc: ManuscriptDocument }
  | {
      kind: 'ready';
      doc: ManuscriptDocument;
      fileName: string;
      skimReportId: string;
      existingSkim: boolean;
      analysis: PatternRunResult;
      api: ApiAnalysisResult | null;
      apiState: 'pending' | 'ready' | 'failed';
      enrichment: LlmEnrichmentResult | null;
      enrichmentState: 'not_run' | 'running' | 'done' | 'failed';
    }
  | { kind: 'error'; message: string };

/**
 * True when the engine state means "this device can't run the optional
 * on-device AI" — no WebGPU at all, or a GPU OOM / device-lost / disposed
 * error. Drives the graceful, non-nagging fallback (the pattern engine is
 * the product; the LLM is optional polish).
 */
function isLlmCapabilityFailure(s: EngineState): boolean {
  if (s.kind === 'absent') return true;
  if (s.kind === 'error') {
    return /gpu|memory|device|disposed|webgpu|out of memory|resource-intensive/i.test(s.message);
  }
  return false;
}

function defaultDraft(): EditorDraft {
  return {
    decision: null,
    rationale: '',
    editorNotes: '',
    selectedReviewerKeys: [],
    manualReviewers: [],
  };
}

function findingsFromAnalysis(analysis: PatternRunResult, skimReportId: string): SkimFindingRecord[] {
  return analysis.findings.map((f) => ({
    id: f.id,
    skimReportId,
    category: f.category,
    severity: f.severity,
    checklistItemId: f.checklistItemId,
    findingText: f.findingText,
    evidencePassage: f.evidencePassage,
    evidenceOffset: f.evidenceOffset,
    editorAction: null,
    layer: 'pattern',
    modelVersion: null,
    createdAt: Date.now(),
  }));
}

function findingsFromEnrichment(
  enrichment: LlmEnrichmentResult,
  skimReportId: string,
): SkimFindingRecord[] {
  return enrichment.extraFindings.map((f: PatternFinding) => ({
    id: f.id,
    skimReportId,
    category: f.category,
    severity: f.severity,
    checklistItemId: f.checklistItemId,
    findingText: f.findingText,
    evidencePassage: f.evidencePassage,
    evidenceOffset: f.evidenceOffset,
    editorAction: null,
    layer: 'llm' as const,
    modelVersion: enrichment.modelId,
    createdAt: Date.now(),
  }));
}

function candidatesFromApi(api: ApiAnalysisResult, skimReportId: string): ReviewerCandidateRecord[] {
  return api.reviewers.candidates.map((c) => ({
    id: c.key,
    skimReportId,
    name: c.name,
    affiliation: c.affiliation,
    orcid: c.orcid,
    email: c.email,
    source: c.source,
    relevantPaperDoi: c.relevantPaperDoi,
    selectedByEditor: false,
  }));
}

function apiRefFindingsToRecords(
  api: ApiAnalysisResult,
  skimReportId: string,
): SkimFindingRecord[] {
  const out: SkimFindingRecord[] = [];
  for (const entry of api.referenceAudit.entries) {
    const doi = entry.doi ?? entry.rawTitle.slice(0, 80);
    if (entry.outcome.status === 'retracted') {
      out.push({
        id: `apiref-retracted-${doi}`,
        skimReportId,
        category: 'references',
        severity: 'red',
        checklistItemId: null,
        findingText: `Reference ${entry.referenceNumber ?? '(unnumbered)'}: cited as active but Crossref reports retraction.`,
        evidencePassage: entry.outcome.work.title || '',
        evidenceOffset: -1,
        editorAction: null,
        layer: 'api',
        modelVersion: null,
        createdAt: Date.now(),
      });
    } else if (entry.outcome.status === 'mismatch') {
      out.push({
        id: `apiref-mismatch-${doi}`,
        skimReportId,
        category: 'references',
        severity: 'amber',
        checklistItemId: null,
        findingText: `Reference ${entry.referenceNumber ?? '(unnumbered)'}: ${entry.outcome.mismatches.join('; ')}.`,
        evidencePassage: '',
        evidenceOffset: -1,
        editorAction: null,
        layer: 'api',
        modelVersion: null,
        createdAt: Date.now(),
      });
    } else if (entry.outcome.status === 'unresolved' && entry.outcome.reason === 'doi_not_found') {
      out.push({
        id: `apiref-not_found-${doi}`,
        skimReportId,
        category: 'references',
        severity: 'amber',
        checklistItemId: null,
        findingText: `Reference ${entry.referenceNumber ?? '(unnumbered)'}: DOI ${entry.doi ?? ''} does not resolve on Crossref.`,
        evidencePassage: '',
        evidenceOffset: -1,
        editorAction: null,
        layer: 'api',
        modelVersion: null,
        createdAt: Date.now(),
      });
    }
  }
  return out;
}

function manualFromRecord(r: ReviewerCandidateRecord): ReviewerCandidate {
  return {
    key: r.id,
    name: r.name,
    affiliation: r.affiliation,
    orcid: r.orcid,
    email: r.email,
    source: 'manual',
    relevantPaperTitle: '(manual addition)',
    relevantPaperDoi: r.relevantPaperDoi,
    relevantPaperYear: null,
  };
}

function syntheticReviewersFromRecords(
  records: ReviewerCandidateRecord[],
): ReviewerSearchResult {
  const candidates: ReviewerCandidate[] = records
    .filter((r) => r.source !== 'manual')
    .map((r) => ({
      key: r.id,
      name: r.name,
      affiliation: r.affiliation,
      orcid: r.orcid,
      email: r.email,
      source: r.source as ReviewerCandidate['source'],
      relevantPaperTitle: '',
      relevantPaperDoi: r.relevantPaperDoi,
      relevantPaperYear: null,
    }));
  return { candidates, degraded: false, partial: false };
}

type EditorAction = NonNullable<SkimFindingRecord['editorAction']>;

export function SkimPanel() {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [draft, setDraft] = useState<EditorDraft>(defaultDraft());
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [engineState, setEngineState] = useState<EngineState>(getLlmClient().getState());
  const [findingActions, setFindingActions] = useState<Record<string, EditorAction | null>>({});
  const [enrichProgress, setEnrichProgress] = useState<{ done: number; total: number } | null>(
    null,
  );
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function refreshFindingActions(reportId: string): Promise<void> {
    const records = await findingsBySkim(reportId);
    const map: Record<string, EditorAction | null> = {};
    for (const r of records) map[r.id] = r.editorAction;
    setFindingActions(map);
  }

  useEffect(() => {
    void getAllSettings().then(setSettings);
  }, []);

  // Apply the chosen LLM provider (+ BYOK key) and subscribe to the *active*
  // engine. Re-runs when settings change so switching engine / entering a key
  // re-points the subscription at the right client. configureLlm is idempotent.
  useEffect(() => {
    if (!settings) return;
    configureLlm({
      provider: settings.llmProvider,
      geminiApiKey: settings.geminiApiKey,
    });
    const client = getLlmClient();
    // The on-device engine becomes "ready" only when the editor explicitly
    // clicks Download & load (a multi-GB download — never automatic). The
    // cloud engine has nothing to download, so once a key is present it is
    // ready immediately; do that here so enrichment can actually run (the
    // Gemini card has no load button by design).
    if (settings.llmProvider === 'gemini' && settings.geminiApiKey.trim()) {
      void client.ensureLoaded().catch(() => {
        /* surfaced via the engine-state subscription */
      });
    }
    setEngineState(client.getState());
    const unsubscribe = client.subscribe((s) => {
      setEngineState(s);
      // Capability failure (no WebGPU, or GPU OOM / device-lost / disposed):
      // remember it so the optional LLM hides gracefully. This is an
      // on-device-only concept — Gemini "absent" just means "no key entered",
      // which is not a hardware failure and must not flip the flag.
      if (settings.llmProvider === 'on-device' && isLlmCapabilityFailure(s)) {
        void setSetting('llmDeviceUnsupported', true);
        setSettings((prev) => (prev ? { ...prev, llmDeviceUnsupported: true } : prev));
      }
    });
    return () => {
      unsubscribe();
    };
  }, [settings]);

  const llmReady = engineState.kind === 'ready';

  const scheduleTextPersist = useCallback(
    (
      skimReportId: string,
      eventType: 'rationale_edited' | 'editor_notes_edited',
      changes: Partial<SkimReportRecord>,
    ) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(async () => {
        const updated = await patch(skimReportId, changes);
        if (updated) {
          await logAuditEvent({
            eventType,
            skimReportId,
            payload: { changedFields: Object.keys(changes) },
          });
        }
      }, 600);
    },
    [],
  );

  async function maybeRunEnrichment(
    skimReportId: string,
    doc: ManuscriptDocument,
    pattern: PatternRunResult,
  ): Promise<void> {
    const client = getLlmClient();
    // `llmDeviceUnsupported` is an ON-DEVICE-only concept (WebGPU missing /
    // GPU OOM). It must not gate the cloud engine — a prior on-device OOM
    // would otherwise permanently suppress Gemini enrichment too.
    const onDeviceBlocked =
      settings?.llmProvider === 'on-device' && settings?.llmDeviceUnsupported;
    if (
      !settings?.llmEnabled ||
      onDeviceBlocked ||
      client.getState().kind !== 'ready'
    )
      return;
    setEnrichProgress({ done: 0, total: 1 });
    setStatus((prev) =>
      prev.kind === 'ready' && prev.skimReportId === skimReportId
        ? { ...prev, enrichmentState: 'running' }
        : prev,
    );
    try {
      const enrichment = await runLlmEnrichment({
        client,
        doc,
        pattern,
        onProgress: (done, total) => setEnrichProgress({ done, total }),
      });
      if (!enrichment) {
        setStatus((prev) =>
          prev.kind === 'ready' && prev.skimReportId === skimReportId
            ? { ...prev, enrichmentState: 'failed' }
            : prev,
        );
        return;
      }
      await upsertFindings(findingsFromEnrichment(enrichment, skimReportId));
      await refreshFindingActions(skimReportId);
      await logAuditEvent({
        eventType: 'analysis_completed',
        skimReportId,
        payload: {
          modelId: enrichment.modelId,
          itemsJudged: enrichment.itemGrades.length,
          reclassified: enrichment.classifier !== null,
          errors: enrichment.errors,
        },
      });
      setStatus((prev) =>
        prev.kind === 'ready' && prev.skimReportId === skimReportId
          ? { ...prev, enrichment, enrichmentState: 'done' }
          : prev,
      );
    } catch (err) {
      console.error('LLM enrichment threw', err);
      setStatus((prev) =>
        prev.kind === 'ready' && prev.skimReportId === skimReportId
          ? { ...prev, enrichmentState: 'failed' }
          : prev,
      );
    }
  }

  async function runAnalysisPipeline(doc: ManuscriptDocument, fileName: string): Promise<void> {
    let report = await findByHash(doc.contentHash);
    const existing = Boolean(report);
    if (!report) {
      report = makeReport({
        manuscriptHash: doc.contentHash,
        manuscriptTitle: doc.title || fileName,
      });
      await upsert(report);
      await logAuditEvent({
        eventType: 'skim_created',
        skimReportId: report.id,
        payload: { manuscriptHash: doc.contentHash, title: report.manuscriptTitle },
      });
      setDraft(defaultDraft());
    } else {
      const cachedCandidates = await candidatesBySkim(report.id);
      const apiSelected = cachedCandidates
        .filter((r) => r.source !== 'manual' && r.selectedByEditor)
        .map((r) => r.id);
      const manuals = cachedCandidates
        .filter((r) => r.source === 'manual')
        .map(manualFromRecord);
      setDraft({
        decision: (report.decisionRecommendation as DecisionLabel | null) ?? null,
        rationale: report.decisionRationale ?? '',
        editorNotes: report.editorNotes,
        selectedReviewerKeys: [...apiSelected, ...manuals.map((m) => m.key)],
        manualReviewers: manuals,
      });
    }

    setStatus({ kind: 'analysing', fileName, doc });
    await new Promise((r) => setTimeout(r, 0));
    const analysis = runPatternAnalysis(doc);

    // Always upsert pattern findings — IDs are stable (finding-id.ts) so prior
    // editor actions on the same finding survive. This also covers the case
    // where the pattern engine evolves and emits new findings on a re-open.
    await upsertFindings(findingsFromAnalysis(analysis, report.id));
    if (!existing) {
      await patch(report.id, {
        studyType: analysis.classifier.label,
        guidelineApplied: analysis.methodology?.guidelineId ?? null,
      });
    }
    await refreshFindingActions(report.id);

    // Show cached candidates immediately so re-opens render fast, even before
    // the API re-runs (per the trade-off documented in ADR-0007 / Sprint 8b).
    let initialApi: ApiAnalysisResult | null = null;
    if (existing) {
      const cached = await candidatesBySkim(report.id);
      initialApi = {
        referenceAudit: { entries: [], degraded: false },
        reviewers: syntheticReviewersFromRecords(cached),
      };
    }

    setStatus({
      kind: 'ready',
      doc,
      fileName,
      skimReportId: report.id,
      existingSkim: existing,
      analysis,
      api: initialApi,
      apiState: 'pending',
      enrichment: null,
      enrichmentState: 'not_run',
    });

    void maybeRunEnrichment(report.id, doc, analysis);

    runApiAnalysis({
      pattern: analysis,
      excludeAuthors: doc.authorSurnames,
      excludeAffiliations: doc.affiliations.map((a) => a.toLowerCase()),
      mailto: settings?.mailto || undefined,
      ncbiApiKey: settings?.ncbiApiKey || undefined,
    })
      .then(async (api) => {
        await bulkInsertCandidates(candidatesFromApi(api, report.id));
        await upsertFindings(apiRefFindingsToRecords(api, report.id));
        await refreshFindingActions(report.id);
        await logAuditEvent({
          eventType: 'analysis_completed',
          skimReportId: report.id,
          payload: {
            referenceAuditDegraded: api.referenceAudit.degraded,
            reviewerDegraded: api.reviewers.degraded,
            reviewerPartial: api.reviewers.partial,
            referenceCount: api.referenceAudit.entries.length,
            candidateCount: api.reviewers.candidates.length,
          },
        });
        setStatus((prev) =>
          prev.kind === 'ready' && prev.skimReportId === report.id
            ? { ...prev, api, apiState: 'ready' }
            : prev,
        );

        // Sprint 9b: claim-support pass — needs both the API audit (for cited-paper
        // abstracts) and a ready LLM. Fires async; merges findings on completion.
        const client = getLlmClient();
        if (client.getState().kind === 'ready' && api.referenceAudit.entries.length > 0) {
          const claimResult = await runClaimSupportPass({ client, doc, audit: api.referenceAudit });
          if (claimResult && claimResult.findings.length > 0) {
            const records = claimResult.findings.map((f) => ({
              id: f.id,
              skimReportId: report.id,
              category: f.category,
              severity: f.severity,
              checklistItemId: f.checklistItemId,
              findingText: f.findingText,
              evidencePassage: f.evidencePassage,
              evidenceOffset: f.evidenceOffset,
              editorAction: null,
              layer: 'llm' as const,
              modelVersion: claimResult.modelId,
              createdAt: Date.now(),
            }));
            await upsertFindings(records);
            await refreshFindingActions(report.id);
          }
          if (claimResult) {
            await logAuditEvent({
              eventType: 'analysis_completed',
              skimReportId: report.id,
              payload: {
                pass: 'claim_support',
                eligibleCount: claimResult.eligibleCount,
                sampledCount: claimResult.sampledCount,
                findingsEmitted: claimResult.findings.length,
                errors: claimResult.errors,
              },
            });
          }
        }
      })
      .catch((err: unknown) => {
        console.error('runApiAnalysis threw unexpectedly', err);
        setStatus((prev) =>
          prev.kind === 'ready' && prev.skimReportId === report.id
            ? { ...prev, apiState: 'failed' }
            : prev,
        );
      });
  }

  function resetForNewManuscript(): void {
    // Soft reset back to the drop zone. Deliberately does NOT unload the LLM:
    // getLlmClient() is a module-level singleton independent of React state, so
    // the loaded model stays resident in memory and the next manuscript
    // enriches immediately — no page reload, no multi-minute model re-init.
    // Persisted reports/findings already live in IndexedDB; re-opening the same
    // PDF restores them via findByHash.
    setStatus({ kind: 'idle' });
    setDraft(defaultDraft());
    setFindingActions({});
    setEnrichProgress(null);
  }

  async function handleFile(buf: ArrayBuffer, name: string) {
    setStatus({ kind: 'extracting', fileName: name, progress: 0 });
    setDraft(defaultDraft());
    setFindingActions({});
    try {
      const doc = await extractFromArrayBuffer(buf, {
        onProgress: (p) => setStatus({ kind: 'extracting', fileName: name, progress: p }),
      });
      await runAnalysisPipeline(doc, name);
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : 'PDF analysis failed.',
      });
    }
  }

  async function handleTextInput(text: string, label: string) {
    setStatus({ kind: 'extracting', fileName: label, progress: 1 });
    setDraft(defaultDraft());
    setFindingActions({});
    try {
      const doc = await extractFromText(text);
      await runAnalysisPipeline(doc, label);
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Text analysis failed.',
      });
    }
  }

  const loaded = status.kind === 'ready' ? status.doc : null;
  const analysis = status.kind === 'ready' ? status.analysis : null;
  const api = status.kind === 'ready' ? status.api : null;
  const apiState = status.kind === 'ready' ? status.apiState : 'pending';
  const skimReportId = status.kind === 'ready' ? status.skimReportId : null;
  const ready = status.kind === 'ready';
  const enrichment = status.kind === 'ready' ? status.enrichment : null;

  // The classifier shown to the user is the enriched one when available.
  const effectiveAnalysis: PatternRunResult | null = analysis
    ? enrichment?.classifier
      ? { ...analysis, classifier: enrichment.classifier }
      : analysis
    : null;

  // The decision shown in the dropdown is `draft.decision ?? suggested`. Export
  // must use the same "effective" decision: otherwise the rubric suggestion is
  // visible but Export stays silently disabled until the editor re-picks the
  // already-shown value. Exporting is the editor's deliberate act; the artifact
  // and audit log record `effectiveDecision`.
  const suggestedDecision = effectiveAnalysis
    ? suggestDecision(effectiveAnalysis, { referenceAudit: api?.referenceAudit ?? null })
    : null;
  const effectiveDecision: DecisionLabel | null = draft.decision ?? suggestedDecision;

  function handleFindingAction(findingId: string, nextAction: EditorAction | null) {
    setFindingActions((prev) => ({ ...prev, [findingId]: nextAction }));
    if (!skimReportId) return;
    void (async () => {
      await updateFindingAction(findingId, nextAction);
      const eventType =
        nextAction === 'confirmed'
          ? 'finding_confirmed'
          : nextAction === 'dismissed'
            ? 'finding_dismissed'
            : 'finding_revisited';
      await logAuditEvent({
        eventType,
        skimReportId,
        payload: { findingId, action: nextAction },
      });
    })();
  }

  function handleDecisionChange(d: DecisionLabel) {
    setDraft((prev) => ({ ...prev, decision: d }));
    if (!skimReportId) return;
    void (async () => {
      await patch(skimReportId, { decisionRecommendation: d });
      await logAuditEvent({
        eventType: 'decision_selected',
        skimReportId,
        payload: { decision: d },
      });
    })();
  }

  function handleRationaleChange(r: string) {
    setDraft((prev) => ({ ...prev, rationale: r }));
    if (!skimReportId) return;
    scheduleTextPersist(skimReportId, 'rationale_edited', { decisionRationale: r });
  }

  function handleNotesChange(notes: string) {
    setDraft((prev) => ({ ...prev, editorNotes: notes }));
    if (!skimReportId) return;
    scheduleTextPersist(skimReportId, 'editor_notes_edited', { editorNotes: notes });
  }

  function handleToggleReviewer(key: string) {
    setDraft((prev) => {
      const set = new Set(prev.selectedReviewerKeys);
      const wasSelected = set.has(key);
      if (wasSelected) set.delete(key);
      else set.add(key);
      if (skimReportId) {
        void setCandidateSelection(key, !wasSelected);
        void logAuditEvent({
          eventType: wasSelected ? 'reviewer_dismissed' : 'reviewer_selected',
          skimReportId,
          payload: { key },
        });
      }
      return { ...prev, selectedReviewerKeys: [...set] };
    });
  }

  function handleAddManualReviewer(c: ReviewerCandidate) {
    setDraft((prev) => ({
      ...prev,
      manualReviewers: [...prev.manualReviewers, c],
      selectedReviewerKeys: [...prev.selectedReviewerKeys, c.key],
    }));
    if (skimReportId) {
      void addManualCandidate({
        id: c.key,
        skimReportId,
        name: c.name,
        affiliation: c.affiliation,
        orcid: c.orcid,
        email: c.email,
        source: 'manual',
        relevantPaperDoi: c.relevantPaperDoi,
        selectedByEditor: true,
      });
      void logAuditEvent({
        eventType: 'reviewer_added_manually',
        skimReportId,
        payload: { name: c.name, hasEmail: Boolean(c.email) },
      });
    }
  }

  function handleRemoveManualReviewer(key: string) {
    setDraft((prev) => ({
      ...prev,
      manualReviewers: prev.manualReviewers.filter((m) => m.key !== key),
      selectedReviewerKeys: prev.selectedReviewerKeys.filter((k) => k !== key),
    }));
    if (skimReportId) {
      void removeCandidate(key);
    }
  }

  async function handleDraftRationale(decision: DecisionLabel): Promise<string | null> {
    if (!loaded || !analysis || !skimReportId) return null;
    const result = await draftDecisionRationale(getLlmClient(), loaded, analysis, decision);
    if (!result.ok) {
      console.warn('Draft failed:', result.error);
      // Surface the real reason in the UI (DecisionCard catches and displays
      // e.message) instead of a generic "Draft failed."
      throw new Error(result.error || 'LLM draft failed (no detail returned).');
    }
    handleRationaleChange(result.rationale);
    await logAuditEvent({
      eventType: 'rationale_edited',
      skimReportId,
      payload: { source: 'llm_draft', promptVersion: result.promptVersion },
    });
    return result.rationale;
  }

  async function handleSaveSettings(
    next: Pick<UserSettings, 'mailto' | 'ncbiApiKey' | 'llmProvider' | 'geminiApiKey'>,
  ) {
    await setSetting('mailto', next.mailto);
    await setSetting('ncbiApiKey', next.ncbiApiKey);
    await setSetting('llmProvider', next.llmProvider);
    await setSetting('geminiApiKey', next.geminiApiKey);
    setSettings((prev) => ({
      ...(prev ?? {
        mailto: '',
        ncbiApiKey: '',
        onboardingCompleted: false,
        llmEnabled: false,
        llmProvider: 'on-device',
        geminiApiKey: '',
        llmModelId: DEFAULT_MODEL_ID,
        llmDeviceUnsupported: false,
      }),
      ...next,
    }));
    await logAuditEvent({
      eventType: 'settings_changed',
      skimReportId: null,
      payload: {
        hasMailto: next.mailto.length > 0,
        hasNcbiKey: next.ncbiApiKey.length > 0,
        llmProvider: next.llmProvider,
        hasGeminiKey: next.geminiApiKey.length > 0,
      },
    });
  }

  async function handleLlmEnableChange(enabled: boolean) {
    await setSetting('llmEnabled', enabled);
    setSettings((prev) => (prev ? { ...prev, llmEnabled: enabled } : prev));
    await logAuditEvent({
      eventType: 'settings_changed',
      skimReportId: null,
      payload: { llmEnabled: enabled },
    });
  }

  async function handleLlmModelChange(modelId: string) {
    await setSetting('llmModelId', modelId);
    setSettings((prev) => (prev ? { ...prev, llmModelId: modelId } : prev));
    await logAuditEvent({
      eventType: 'settings_changed',
      skimReportId: null,
      payload: { llmModelId: modelId },
    });
  }

  // Editor-initiated escape hatch from the graceful "device unsupported"
  // state (e.g. they freed GPU memory or switched to a smaller model).
  async function handleLlmTryAnyway() {
    await setSetting('llmDeviceUnsupported', false);
    setSettings((prev) => (prev ? { ...prev, llmDeviceUnsupported: false } : prev));
    try {
      await getLlmClient().ensureLoaded(settings?.llmModelId);
    } catch {
      // Re-surfaces via the engine-state subscription if it fails again.
    }
  }

  async function handleDismissOnboarding() {
    await setSetting('onboardingCompleted', true);
    setSettings((prev) =>
      prev ? { ...prev, onboardingCompleted: true } : prev,
    );
  }

  function buildExportInput(): ExportInput | null {
    if (!ready || status.kind !== 'ready') return null;
    return {
      manuscript: status.doc,
      fileName: status.fileName,
      pattern: effectiveAnalysis ?? status.analysis,
      referenceAudit: status.api?.referenceAudit ?? null,
      reviewers: status.api?.reviewers ?? null,
      draft: { ...draft, decision: effectiveDecision },
      skimReportId: status.skimReportId,
      generatedAt: new Date().toISOString(),
    };
  }

  async function handleCopyEmail(): Promise<boolean> {
    const input = buildExportInput();
    if (!input || !skimReportId) return false;
    const email = generateEmail(input);
    const ok = await copyToClipboard(`${email.subject}\n\n${email.body}`);
    await logAuditEvent({
      eventType: 'exported',
      skimReportId,
      payload: { format: 'email', success: ok },
    });
    return ok;
  }

  function handleDownloadPdf() {
    const input = buildExportInput();
    if (!input || !skimReportId) return;
    const blob = generatePdf(input);
    const safeTitle = (input.manuscript.title || input.fileName)
      .replace(/[^a-z0-9-]+/gi, '-')
      .slice(0, 60);
    triggerDownload(blob, `skim-${safeTitle}.pdf`);
    void logAuditEvent({
      eventType: 'exported',
      skimReportId,
      payload: { format: 'pdf' },
    });
  }

  function handleDownloadJson() {
    const input = buildExportInput();
    if (!input || !skimReportId) return;
    const blob = generateJson(input);
    triggerDownload(blob, `skim-${skimReportId.slice(0, 8)}.json`);
    void logAuditEvent({
      eventType: 'exported',
      skimReportId,
      payload: { format: 'json' },
    });
  }

  const showOnboarding = settings !== null && !settings.onboardingCompleted;
  const showScannedFallback = ready && status.doc.looksScanned;
  const showLlmCard = settings !== null;

  return (
    <main className="flex h-screen flex-col text-slate-800">
      <header className="sticky top-0 z-10 border-b border-slate-200/80 bg-white/95 px-4 py-3 shadow-header backdrop-blur">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-[13px] font-bold text-white shadow-sm"
            >
              M
            </span>
            <div className="leading-tight">
              <h1 className="text-[15px] font-semibold tracking-tight text-slate-900">
                ManuscriptSkim
              </h1>
              <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
                Editorial triage assistant
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {(status.kind === 'ready' ||
              status.kind === 'error' ||
              status.kind === 'analysing') && (
              <button
                type="button"
                onClick={resetForNewManuscript}
                className="ms-btn ms-btn-secondary ms-btn-xs"
                title="Skim another manuscript without reloading — keeps the LLM loaded"
              >
                + New manuscript
              </button>
            )}
            <AboutPanel />
            <SettingsPanel settings={settings} onSave={handleSaveSettings} />
          </div>
        </div>
        {status.kind === 'idle' && (
          <p className="mt-0.5 text-xs text-slate-500">
            Drop a PDF or open one from your email.
          </p>
        )}
        {status.kind === 'extracting' && (
          <p className="mt-0.5 text-xs text-slate-500">
            Extracting {status.fileName}… {Math.round(status.progress * 100)}%
          </p>
        )}
        {status.kind === 'analysing' && (
          <p className="mt-0.5 text-xs text-slate-500">
            Running pattern analysis on {status.fileName}…
          </p>
        )}
        {status.kind === 'ready' && (
          <p className="mt-0.5 truncate text-xs text-slate-500" title={status.doc.contentHash}>
            {status.doc.title || status.fileName} · {status.doc.pages.length} pp ·{' '}
            {status.doc.wordCount.toLocaleString()} words
            {status.existingSkim && (
              <span className="ml-1 text-slate-400">· prior skim restored</span>
            )}
            {status.apiState === 'pending' && (
              <span className="ml-1 text-slate-400">· fetching external data…</span>
            )}
            {status.enrichmentState === 'running' && (
              <span className="ml-1 text-slate-400">
                · LLM enriching…
                {enrichProgress && enrichProgress.total > 0
                  ? ` ${enrichProgress.done}/${enrichProgress.total}`
                  : ''}
              </span>
            )}
          </p>
        )}
        {status.kind === 'error' && (
          <p className="mt-0.5 text-xs text-red-600">{status.message}</p>
        )}
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {showOnboarding && <OnboardingCard onDismiss={handleDismissOnboarding} />}

        {showLlmCard && settings && (
          <LlmStatusCard
            enabled={settings.llmEnabled}
            provider={settings.llmProvider}
            geminiKeySet={settings.geminiApiKey.trim().length > 0}
            modelId={settings.llmModelId}
            deviceUnsupported={settings.llmDeviceUnsupported}
            onEnableChange={handleLlmEnableChange}
            onModelChange={handleLlmModelChange}
            onTryAnyway={handleLlmTryAnyway}
          />
        )}

        {(status.kind === 'idle' || status.kind === 'error') && (
          <>
            <PdfDropZone onFile={handleFile} />
            <TextInputCard reason="fallback" onSubmit={handleTextInput} />
          </>
        )}

        {showScannedFallback && (
          <TextInputCard reason="scanned" onSubmit={handleTextInput} />
        )}

        <StudyTypeCard manuscript={loaded} analysis={effectiveAnalysis} />
        {status.kind === 'ready' && (
          <EnrichmentStatusCard
            enabled={Boolean(settings?.llmEnabled)}
            deviceUnsupported={
              settings?.llmProvider === 'on-device' &&
              Boolean(settings?.llmDeviceUnsupported)
            }
            ready={llmReady}
            state={status.enrichmentState}
            result={status.enrichment}
            progress={enrichProgress}
          />
        )}
        <MethodologyChecklist
          manuscript={loaded}
          analysis={effectiveAnalysis}
          itemGrades={enrichment?.itemGrades ?? null}
          findingActions={findingActions}
          onFindingAction={handleFindingAction}
        />
        <IntegrityFindings
          manuscript={loaded}
          analysis={effectiveAnalysis}
          referenceAudit={api?.referenceAudit ?? null}
          apiState={apiState}
          findingActions={findingActions}
          onFindingAction={handleFindingAction}
        />
        <ReviewerSearchPanel
          manuscript={loaded}
          analysis={effectiveAnalysis}
          reviewers={api?.reviewers ?? null}
          apiState={apiState}
          selectedKeys={new Set(draft.selectedReviewerKeys)}
          manualReviewers={draft.manualReviewers}
          onToggleSelect={handleToggleReviewer}
          onAddManual={handleAddManualReviewer}
          onRemoveManual={handleRemoveManualReviewer}
        />
        <DecisionCard
          manuscript={loaded}
          analysis={effectiveAnalysis}
          referenceAudit={api?.referenceAudit ?? null}
          decision={draft.decision}
          rationale={draft.rationale}
          editorNotes={draft.editorNotes}
          llmReady={llmReady}
          onDecisionChange={handleDecisionChange}
          onRationaleChange={handleRationaleChange}
          onNotesChange={handleNotesChange}
          onDraftRationale={handleDraftRationale}
        />
        {ready && (
          <ExportBar
            onCopyEmail={handleCopyEmail}
            onDownloadPdf={handleDownloadPdf}
            onDownloadJson={handleDownloadJson}
            disabled={!effectiveDecision}
          />
        )}
      </div>

      <footer className="flex items-center justify-between border-t border-slate-200/80 bg-white/90 px-4 py-2 text-[10px] text-slate-400 backdrop-blur">
        <span className="inline-flex items-center gap-1.5">
          <span
            className={`ms-dot ${
              llmReady
                ? 'bg-emerald-500'
                : settings?.llmEnabled
                  ? 'bg-amber-400'
                  : 'bg-slate-300'
            }`}
          />
          {llmReady
            ? 'LLM enabled'
            : settings?.llmEnabled
              ? 'LLM enabled (not loaded)'
              : 'Pattern-only · LLM not loaded'}
        </span>
        <span className="font-medium text-slate-400">
          v1.0.0 · {typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'}
        </span>
      </footer>
    </main>
  );
}
